import moment from 'moment';
import 'moment-timezone';

export type DbEventDateFields = {
  e_date?: string | null;
  e_time?: string | null;
  e_date_time_zone?: string | null;
  e_time_zone?: string | null;
};

const DEFAULT_ZONE = 'America/Chicago';

/** Human labels like "Monday ~ Oct 06 ~ 7:00 pm CDT" are display-only, not parseable. */
export const isMachineReadableEventDateTimeZone = (value?: string | null): boolean => {
  const raw = String(value || '').trim();
  if (!raw || raw.includes('~')) return false;
  return /^\d{4}-\d{2}-\d{2}/.test(raw);
};

const hasUtcOffset = (value: string) => /([zZ]|[+-]\d{2}:?\d{2})$/.test(value.trim());

/**
 * Resolve venue-local wall-clock parts exactly as stored in DB.
 * Prefer e_date_time_zone (local ISO) and e_time; fall back to e_date + e_time_zone.
 */
export const resolveDbEventLocalParts = (
  fields: DbEventDateFields,
): { dateYmd: string; timeHm: string; zone: string } | null => {
  const zone = String(fields.e_time_zone || '').trim() || DEFAULT_ZONE;
  const localIso = isMachineReadableEventDateTimeZone(fields.e_date_time_zone)
    ? String(fields.e_date_time_zone).trim()
    : '';
  const eDate = String(fields.e_date || '').trim();
  const eTime = String(fields.e_time || '').trim();

  let dateYmd = '';
  let timeHm = '';

  // 1) Exact local calendar/time from e_date_time_zone ISO (no UTC reinterpretation of the digits)
  if (localIso) {
    const localMatch = localIso.match(/^(\d{4}-\d{2}-\d{2})(?:[T\s](\d{2}):(\d{2}))?/);
    if (localMatch) {
      dateYmd = localMatch[1];
      if (localMatch[2] && localMatch[3]) {
        timeHm = `${localMatch[2]}:${localMatch[3]}`;
      }
    }
  }

  // 2) Exact e_time from DB when present
  if (eTime) {
    const timeMatch = eTime.match(/^(\d{2}):(\d{2})/);
    if (timeMatch) {
      timeHm = `${timeMatch[1]}:${timeMatch[2]}`;
    }
  }

  // 3) Derive missing parts from UTC e_date in venue timezone
  if ((!dateYmd || !timeHm) && eDate) {
    if (/^\d{4}-\d{2}-\d{2}$/.test(eDate)) {
      if (!dateYmd) dateYmd = eDate;
    } else {
      const local = hasUtcOffset(eDate)
        ? moment.utc(eDate).tz(zone)
        : moment.tz(eDate, zone);
      if (local.isValid()) {
        if (!dateYmd) dateYmd = local.format('YYYY-MM-DD');
        if (!timeHm) timeHm = local.format('HH:mm');
      } else {
        const fallback = eDate.match(/^(\d{4}-\d{2}-\d{2})(?:[T\s](\d{2}):(\d{2}))?/);
        if (fallback) {
          if (!dateYmd) dateYmd = fallback[1];
          if (!timeHm && fallback[2] && fallback[3]) {
            timeHm = `${fallback[2]}:${fallback[3]}`;
          }
        }
      }
    }
  }

  if (!dateYmd && !timeHm) return null;
  return { dateYmd, timeHm, zone };
};

export const formatDbEventDate = (
  fields: DbEventDateFields,
  style: 'short' | 'medium' | 'long' = 'short',
): string => {
  const parts = resolveDbEventLocalParts(fields);
  if (!parts?.dateYmd) return 'TBD';

  const local = moment.tz(`${parts.dateYmd} ${parts.timeHm || '00:00'}`, 'YYYY-MM-DD HH:mm', parts.zone);
  if (!local.isValid()) {
    const match = parts.dateYmd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return 'TBD';
    if (style === 'short') return `${Number(match[2])}/${Number(match[3])}/${match[1]}`;
    return parts.dateYmd;
  }

  if (style === 'long') return local.format('dddd, MMMM D, YYYY');
  if (style === 'medium') return local.format('ddd, DD MMM YYYY');
  return local.format('M/D/YYYY');
};

export const formatDbEventTime = (
  fields: DbEventDateFields,
  style: 'short' | 'withZone' = 'short',
): string => {
  const parts = resolveDbEventLocalParts(fields);
  if (!parts?.timeHm) return '';

  const local = moment.tz(`${parts.dateYmd || '1970-01-01'} ${parts.timeHm}`, 'YYYY-MM-DD HH:mm', parts.zone);
  if (!local.isValid()) {
    const [h, m] = parts.timeHm.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${String(m).padStart(2, '0')} ${period}`;
  }

  if (style === 'withZone') return local.format('hh:mm A z');
  return local.format('h:mm A');
};

export const formatDbEventDateTime = (
  fields: DbEventDateFields,
  style: 'short' | 'medium' | 'long' = 'medium',
): string => {
  const date = formatDbEventDate(fields, style === 'short' ? 'short' : style);
  const time = formatDbEventTime(fields, style === 'medium' ? 'withZone' : 'short');
  if (date === 'TBD') return time || 'Date TBD';
  return time ? `${date} • ${time}` : date;
};

export const formatDbEventDateYmd = (fields: DbEventDateFields): string => {
  return resolveDbEventLocalParts(fields)?.dateYmd || '';
};
